/* eslint-disable no-useless-escape */
import React, {
  useState, useEffect, Dispatch, useContext,
} from 'react';
import {
  FormControl,
  FormHelperText,
  Input,
  InputLabel,
  Button,
  Box,
  MenuItem,
  Select,
  Typography,
  CircularProgress,
  useTheme,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import BusinessIcon from '@mui/icons-material/Business';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import MailIcon from '@mui/icons-material/Mail';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import Modal from 'components/Modal';
import { DICTIONARY } from 'shared/constants';
import PersonService from 'services/person';
import { Role } from 'models';
import { ROUTES } from 'shared/routes';
import { useNavigate } from 'react-router-dom';
import { resolvers } from 'shared/enums';
import {
  emailIsInWhitelist, getCacheWithExpiration, isValidEmail, setCacheWithExpiration,
} from 'shared/utilities';
import { getFirm, getCompany } from 'graphql/queries';
import { Company, GetCompanyQuery, GetFirmQuery } from 'API';
import FormService from 'services/form';
import AutocompleteWithSearch from 'components/forms/AutocompleteWithSearch';
import IconRow from 'components/IconRow';
import IconRowCell from 'components/IconRowCell';
import { FIRM_COMPANY } from 'services/firm';
import SidebarContext from 'shared/contexts/SidebarContext';
import { client } from 'lib/graphql-client';
import { GraphQLResult } from '@aws-amplify/api';

const roleResolver = resolvers.Role;

const getCreatePersonStyles = (theme: Theme) => ({
  error: {
    color: theme.palette.error.main,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: '1rem',
    rowGap: '1rem',
  },
  formControl: {
    width: '100%',
  },
  nameRowIcon: {
    marginLeft: '0.25rem',
    marginTop: '1.5rem',
  },
  submit: {
    display: 'flex',
    marginLeft: 'auto',
  },
});

interface Props {
  open: boolean;
  setOpen: Dispatch<boolean>;
  createPersonConfig: PersonCreateConfig;
}

const CreateNewPerson: React.FC<Props> = (
  { open, setOpen, createPersonConfig }: Props,
) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const styles = getCreatePersonStyles(theme);
  const {
    companyID, companyName, createRole, immutableRole,
  } = createPersonConfig;
  const initialFormData = {
    role: createPersonConfig.createRole,
    companyID: companyID || null,
    slug: '',
    firstname: '',
    lastname: '',
    email: null,
    personalEmail: null,
    personalPhone: null,
  };
  const [company, setCompany] = useState<Company | undefined>();
  const [domainWhitelist, setDomainWhitelist] = useState<Array<string>>([]);
  const [formData, setFormData] = useState<CreatePersonInput>(initialFormData);
  const [error, setError] = useState(false);
  const [message, setMessage] = useState('');
  const [emailError, setEmailError] = useState('');
  const [personalPhoneError, setPersonalPhoneError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isValidForm, setIsValidForm] = useState(true);
  const { setNewItem } = useContext(SidebarContext);

  const roleOptions = [Role.FOREIGNNATIONAL, Role.EMPLOYER, Role.TEAMMEMBER];

  const requiresEmail = [
    Role.EMPLOYER, Role.TEAMMEMBER,
  ].includes(formData.role);

  const requiresCompany = formData.role === Role.EMPLOYER;

  let title = '';

  if (immutableRole && createRole === Role.EMPLOYER) {
    title = 'Add Employer';
  } else if (immutableRole && createRole === Role.TEAMMEMBER) {
    title = 'Add Team Member';
  } else {
    title = DICTIONARY.CreateNewUserTitle;
  }

  const handleFormUpdate = (key: string, value: string | boolean | Role) => {
    setFormData(
      {
        ...formData,
        [key]: value,
      },
    );
  };

  const handleCompanySelect = (e: any, v: any) => {
    setCompany(v);
    setFormData({
      ...formData,
      companyID: v?.id || null,
    });
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    let slug = '';

    try {
      const {
        email, personalEmail, personalPhone, ...personInput
      } = formData;
      slug = await PersonService
        .generateSlug(`${formData.firstname} ${formData.lastname}`);
      personInput.slug = slug;
      if (email) {
        personInput.email = email;
      }
      if (personalEmail) {
        personInput.personalEmail = personalEmail;
      }
      if (personalPhone) {
        personInput.personalPhone = personalPhone;
      }

      if (requiresCompany) {
        personInput.newsletterModalViewCount = 0;
      }

      const newPerson = await PersonService.create(
        personInput,
        formData.role,
        company?.id || companyID,
      );

      setNewItem(newPerson);
      setIsLoading(false);
      setOpen(false);
      navigate(`${ROUTES.people}/${slug}`);
    } catch (err: any) {
      setError(true);
      setIsLoading(false);
      setMessage(err.message || 'There was an error.');
    }
  };

  useEffect(() => {
    const {
      firstname, lastname, email, personalEmail, personalPhone,
    } = formData;

    switch (formData.role) {
      case Role.FOREIGNNATIONAL:
        setFormData({
          ...formData,
          companyID: null,
          email: null,
        });
        setIsValidForm(!!firstname && !!lastname);
        break;

      case Role.EMPLOYER:
        setIsValidForm(
          !!firstname
          && !!lastname
          && !!formData?.companyID
          && isValidEmail(email)
          && (!personalEmail || (isValidEmail(personalEmail)))
          && (!personalPhone || !FormService.phoneValidator(formData.personalPhone))
          && emailIsInWhitelist(email, domainWhitelist),
        );
        break;

      case Role.TEAMMEMBER:
        setIsValidForm(
          !!firstname
          && !!lastname
          && isValidEmail(email)
          && emailIsInWhitelist(email, domainWhitelist),
        );

        break;

      default:

        break;
    }
  }, [formData.role, formData.email, formData.firstname, formData.lastname, formData.personalEmail, formData.personalPhone]);

  useEffect(() => {
    let ignore = false;
    const { role } = formData;
    const getDomains = async () => {
      const response = await getCacheWithExpiration(
        'firmDomain',
        () => client.graphql({
          query: getFirm,
          variables: { id: FIRM_COMPANY.id },
        }) as unknown as GraphQLResult<GetFirmQuery>,
        60 * 60 * 1000,
      );

      if (!ignore) {
        setDomainWhitelist(response.data?.getFirm?.domainWhitelist || []);
      }
    };

    if (role === Role.TEAMMEMBER) {
      getDomains();
    }

    if (formData.role !== Role.EMPLOYER) {
      setCompany(undefined);
    }

    setEmailError('');

    return () => { ignore = true; };
  }, [formData.role]);

  useEffect(() => {
    let ignore = false;
    const getDomains = async () => {
      const cacheKey = `domains${formData.companyID}`;

      const cacheValue = await getCacheWithExpiration(cacheKey);
      if (cacheValue) {
        setDomainWhitelist(cacheValue);
      }

      const response = await client.graphql({
        query: getCompany,
        variables: { id: formData.companyID },
      }) as unknown as GraphQLResult<GetCompanyQuery>;

      if (!ignore) {
        setDomainWhitelist(response.data?.getCompany?.domainWhitelist || []);
        setCacheWithExpiration(
          cacheKey,
          response.data?.getCompany?.domainWhitelist || [],
          Date.now() + 30 * 60 * 60 * 1000,
        );
      }
    };

    if (formData.companyID) {
      getDomains();
    }

    return () => { ignore = true; };
  }, [formData.companyID]);

  useEffect(() => {
    setFormData(initialFormData);
  }, [open]);

  useEffect(() => {
    if (requiresCompany && requiresEmail && (!company && !companyID)) {
      setEmailError(DICTIONARY.SelectCompanyFirst);
    } else if (requiresEmail) {
      if (isValidEmail(formData.email)
        && !emailIsInWhitelist(formData.email, domainWhitelist)) {
        setEmailError(DICTIONARY.WhitelistMismatchError);
      } else {
        setEmailError('');
      }
    }
  }, [formData.email, company]);

  useEffect(() => {
    if (!requiresCompany) {
      setPersonalPhoneError('');

      return;
    }
    const value = formData.personalPhone ?? '';
    const phoneError = value ? FormService.phoneValidator(value) : '';
    setPersonalPhoneError(phoneError);
  }, [requiresCompany, formData.personalPhone]);

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title={title}
      titleIcon={PersonOutlineIcon}
    >
      {isLoading ? (
        <CircularProgress />
      ) : (
        <Box sx={styles.form}>
          {!immutableRole && (
            <>
              <InputLabel id="role-select-label">
                {DICTIONARY.CreateNewUserRolePrompt}
              </InputLabel>
              <FormControl sx={styles.formControl} variant="outlined">
                <Select
                  id="role-select-input"
                  labelId="role-select-label"
                  variant="outlined"
                  value={formData.role}
                  onChange={(e) => handleFormUpdate('role', e.target.value as Role)}
                >
                  {roleOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {roleResolver(option)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}

          {immutableRole && createRole === Role.EMPLOYER && companyName && (
            <Typography>
              {`You are adding an employer to ${companyName}.`}
            </Typography>
          )}

          {immutableRole && createRole === Role.TEAMMEMBER && (
            <Typography>
              {`You are adding a colleague to ${FIRM_COMPANY.name}.`}
            </Typography>
          )}

          {requiresCompany && !companyID && (
            <IconRow isInput>
              <IconRowCell type="icon">
                <BusinessIcon fontSize="small" />
              </IconRowCell>
              <IconRowCell type="text">
                <AutocompleteWithSearch
                  elementId="search-company-autocomplete"
                  label="Company"
                  onChange={handleCompanySelect}
                  type="Company"
                  value={company}
                  required
                />
              </IconRowCell>
            </IconRow>
          )}

          <IconRow isInput>
            <IconRowCell type="icon">
              <PersonIcon fontSize="small" />
            </IconRowCell>
            <IconRowCell type="text">
              <FormControl sx={styles.formControl}>
                <InputLabel htmlFor="fname-input" required>
                  {DICTIONARY.Firstname}
                </InputLabel>
                <Input
                  id="fname-input"
                  value={formData.firstname}
                  onChange={(e) => handleFormUpdate('firstname', e.target.value)}
                />
              </FormControl>
            </IconRowCell>
          </IconRow>

          <IconRow isInput>
            <IconRowCell type="icon">
              <PersonIcon fontSize="small" />
            </IconRowCell>
            <IconRowCell type="text">
              <FormControl sx={styles.formControl}>
                <InputLabel htmlFor="lname-input" required>
                  {DICTIONARY.Lastname}
                </InputLabel>
                <Input
                  id="lname-input"
                  value={formData.lastname}
                  onChange={(e) => handleFormUpdate('lastname', e.target.value)}
                />
              </FormControl>
            </IconRowCell>
          </IconRow>

          {requiresEmail && (
            <IconRow isInput>
              <IconRowCell type="icon">
                <MailIcon fontSize="small" />
              </IconRowCell>
              <IconRowCell type="text">
                <FormControl sx={styles.formControl}>
                  <InputLabel htmlFor="email-input" required>
                    {DICTIONARY.Email}
                  </InputLabel>
                  <Input
                    id="email-input"
                    value={formData.email || ''}
                    onChange={(e) => handleFormUpdate('email', e.target.value)}
                  />
                  {emailError && (
                    <FormHelperText error>{emailError}</FormHelperText>
                  )}
                </FormControl>
              </IconRowCell>
            </IconRow>
          )}

          {requiresCompany && (
            <IconRow isInput>
              <IconRowCell type="icon">
                <MailIcon fontSize="small" />
              </IconRowCell>
              <IconRowCell type="text">
                <FormControl sx={styles.formControl}>
                  <InputLabel htmlFor="personal-email-input">
                    {DICTIONARY.PersonalEmail}
                  </InputLabel>
                  <Input
                    id="personal-email-input"
                    value={formData.personalEmail || ''}
                    onChange={(e) => handleFormUpdate('personalEmail', e.target.value)}
                  />
                </FormControl>
              </IconRowCell>
            </IconRow>
          )}

          {requiresCompany && (
            <IconRow isInput>
              <IconRowCell type="icon">
                <PhoneIcon fontSize="small" />
              </IconRowCell>
              <IconRowCell type="text">
                <FormControl sx={styles.formControl}>
                  <InputLabel htmlFor="personal-phone-input">
                    {DICTIONARY.PersonalPhone}
                  </InputLabel>
                  <Input
                    id="personal-phone-input"
                    value={formData.personalPhone || ''}
                    onChange={(e) => handleFormUpdate('personalPhone', e.target.value)}
                  />
                  {personalPhoneError && (
                    <FormHelperText error>{personalPhoneError}</FormHelperText>
                  )}
                </FormControl>
              </IconRowCell>
            </IconRow>
          )}

          {message && (
            <Typography
              paragraph
              id="create-user-message"
              sx={[error ? styles.error : {}]}
            >
              {message}
            </Typography>
          )}

          <Button
            onClick={handleSubmit}
            color="primary"
            disabled={!isValidForm}
            id="create-user"
            variant="contained"
            sx={styles.submit}
          >
            {DICTIONARY.CreateNewUserButton}
          </Button>
        </Box>
      )}
    </Modal>
  );
};

export default CreateNewPerson;
